import styled from '@emotion/styled';
import { licensePriorityOrder } from '@shared/constants/FeatureFlagValues';
import { useCurrentUserStore } from '@shared/hooks/state/CurrentUserContainer';
import { ProductDisplayModel, PurchaseOption } from '@shared/models/components/ProductDisplayModel';
import { ProductSupportedFeaturesModel } from '@shared/models/components/ProductSupportedFeaturesModel';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import Button from '../core/button/CoreButton';
import { CardSmall, IconWrap, StatusChip, WrapCardText } from '../core/card/CommonCardStyles';
import Icons from '../core/images/Icons';
import { LabelMedium, LightSmall, LightXSmall, TitleMedium, TitleSmall } from '../core/text/Typography';
import LicenseTypeLogo from '../license/LicenseTypeLogo';
import { getUpgradeEntryPath } from '../organization/billing/upgradeNavigation';

export interface IProductCardProps {
    product: ProductDisplayModel;
    purchaseOption?: PurchaseOption;
    supportedFeatures: ProductSupportedFeaturesModel[];
    statusText?: string;
    $variant?: 'default' | 'small';
    organizationId?: string;
}

export default function ProductCard({
    product,
    purchaseOption,
    supportedFeatures,
    statusText,
    $variant = 'default',
    organizationId,
}: IProductCardProps) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const hasNoOrgMemberships = useCurrentUserStore((state) => state.hasNoOrgMemberships);
    const userLicenseType = useCurrentUserStore((state) => state.userLicenseType);

    // Determine if we should show trial upgrade option (ie Plus Trial -> Pro Trial)
    const currPriorityIdx = licensePriorityOrder.indexOf(userLicenseType);
    const nextPriorityIdx = licensePriorityOrder.indexOf(product.licenseType);
    const isTrialUpgrade = currPriorityIdx !== -1 && nextPriorityIdx !== -1 && nextPriorityIdx > currPriorityIdx;
    const showTrial = hasNoOrgMemberships || isTrialUpgrade;

    let label = t('common:billing.subscription.billedMonthly');
    if (purchaseOption?.type === 'yearly') {
        label = t('common:billing.subscription.billedYearly');
    }

    const onPurchaseClickForOrg = () => {
        if (!organizationId || !purchaseOption?.productId) {
            return;
        }
        navigate(getUpgradeEntryPath({ organizationId, productId: purchaseOption.productId }));
    };

    return (
        <CardSmall title={t(product.title)}>
            {statusText && <StatusChip>{statusText}</StatusChip>}
            <StyledIconWrap $variant={$variant}>
                <LicenseTypeLogo licenseType={product.licenseType} size={150} />
            </StyledIconWrap>
            <StyledWrapCardText>
                <TitleSmall>{t(product.title)}</TitleSmall>
                {purchaseOption && (
                    <TitleMedium as="p">
                        ${purchaseOption.price} <LightSmall as="span">{label}</LightSmall>
                    </TitleMedium>
                )}
                <FeatureWrap>
                    {supportedFeatures.map((sf, index) => (
                        <FeatureGroup key={`supportedFeature_${sf.tag}_${index}`}>
                            <LabelMedium>{sf.tag}</LabelMedium>
                            {sf.features.map((feature, idx) => (
                                <FeatureItem key={`feature_${feature}_${idx}`}>
                                    <Icons name="check" size={16} />
                                    <LightXSmall>{t(feature)}</LightXSmall>
                                </FeatureItem>
                            ))}
                        </FeatureGroup>
                    ))}
                </FeatureWrap>
                <Footer>
                    <FooterLinks>
                        {purchaseOption &&
                            (hasNoOrgMemberships ? (
                                <Button
                                    key={'purchaseLink'}
                                    href={purchaseOption.defaultPaymentLink}
                                    target="_blank"
                                    $color="secondary">
                                    {t('common:purchase')}
                                </Button>
                            ) : (
                                <Button key={'purchaseLink'} onClick={() => onPurchaseClickForOrg()} $color="secondary">
                                    {t('common:purchase')}
                                </Button>
                            ))}
                        {product.trial && showTrial && (
                            <Button key={'trialLink'} href={product.trial} target="_blank" $color="simple">
                                {t('common:license.trial.start')}
                            </Button>
                        )}
                    </FooterLinks>
                </Footer>
            </StyledWrapCardText>
        </CardSmall>
    );
}

const Footer = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    flex-grow: 1;
    width: 100%;
`;
const FooterLinks = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center; /* Center-align the links horizontally */
`;

const FeatureWrap = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    gap: 2rem;
    flex-grow: 1;
    margin: 1rem 0;
`;

const FeatureGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
`;

const FeatureItem = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 0.5rem;
    color: ${({ theme }) => theme.color.link};
`;

const StyledIconWrap = styled(IconWrap)<{ $variant?: 'default' | 'small' }>`
    padding: 0 0;
    min-height: auto;
    svg {
        padding: ${(props) => (props.$variant === 'small' ? '0.75rem' : '0')};
    }
`;

const StyledWrapCardText = styled(WrapCardText)`
    padding: 0.5rem 1.25rem 1.75rem 1.25rem;
`;
